import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import classnames from 'classnames';
import FrequencyFieldRedesign from 'js/components/elements/FrequencyFieldRedesign/FrequencyFieldRedesign';
import { Field, Resource, Resources } from '@nexla/@types';
import { PillTabs } from 'new/elements/PillTabs';
import ResourceSelectionButton from 'new/elements/ResourceSelectionButton';
import { RESOURCE_TYPE_SINK, RESOURCE_TYPE_SOURCE } from 'js/constants/resourceTypes';
import {
  FlowTrigger,
  FlowTriggerEventType,
  FlowTriggerHelper,
} from 'js/components/data_sources/add_ds_form/MonitoringInterval/FlowTriggerHelper';
import { useFlowTrigger } from 'js/components/data_sources/add_ds_form/MonitoringInterval/useFlowTrigger';
import styles from './MonitoringInterval.module.scss';
import { isRagNode } from 'new/utils';
import ExternalTriggerConfig from 'js/components/elements/ExternalTriggerConfig/ExternalTriggerConfig';
import { renderFeatureBadge } from 'js/constants/featureBadges';

const descriptionCron =
  'Set a schedule to automatically check for new or updated data for processing.';

type Props = {
  field: Field;
  source?: Resource;
  config: Record<string, any>;
  onConfigChanged(data: any, fieldName: string, value: any): void;
  onUserModified?: () => void;
  monitorIntervalContainerClassname?: string;
  showExternalTrigger?: boolean;
  showFlowTriggers?: boolean;
};

const tabs = (name: string, showExternalTrigger?: boolean) => {
  const tabs = [
    { label: 'Scheduled Ingestion', value: name },
    { label: 'On Source Completion', value: FlowTrigger.SOURCE },
    { label: 'On Destination Completion', value: FlowTrigger.SINK },
  ];
  // only add External Trigger for in_memory flow AND if the feature flag is enabled
  if (showExternalTrigger) {
    tabs.push({
      label: (
        <>
          On External Trigger{' '}
          {renderFeatureBadge('EXTERNAL_TRIGGER', {
            variant: 'text',
            size: 'small',
            sx: {
              marginLeft: '4px',
              fontSize: '0.85em',
              opacity: 0.8,
              position: 'relative',
              top: -4,
              color: 'inherit !important',
            },
          })}
        </>
      ),
      value: FlowTrigger.EXTERNAL,
    });
  }

  return tabs;
};

const {
  getTriggers,
  getDescriptionTrigger,
  createWriteTrigger,
  extractFlowTriggerFromSource,
  getDefaultOnceCron,
  isInboundTrigger,
  getInboundTriggers,
  getTriggeringResourceId,
} = FlowTriggerHelper;

/** Which schedule mode the source is in now, from the triggers that start it. */
const getModeFromSource = (source: any, cronFieldName?: string) => {
  const eventType = getInboundTriggers(source)[0]?.triggering_event_type;
  if (eventType === FlowTriggerEventType.SOURCE) return FlowTrigger.SOURCE;
  if (eventType === FlowTriggerEventType.SINK) return FlowTrigger.SINK;
  if (source?.['adaptive_flow'] === true) return FlowTrigger.EXTERNAL;
  return cronFieldName;
};

const MonitoringInterval = ({
  field,
  onConfigChanged,
  onUserModified,
  config,
  source = undefined,
  monitorIntervalContainerClassname = '',
  showExternalTrigger = false,
  showFlowTriggers = true,
}: Props) => {
  const { sinks, sources, loading } = useFlowTrigger(source);

  const previousCronRef = useRef<any>(
    config?.[field?.name] === 'once' ? undefined : config?.[field?.name],
  );

  const [selectedType, setSelectType] = useState(() => getModeFromSource(source, field?.name));

  const [selectedFlowTriggers, setSelectedFlowTriggers] = useState(
    extractFlowTriggerFromSource(source),
  );

  // The source can first arrive without flow_triggers (the flows-list payload omits
  // them) and gain them once the full source loads. Follow it until the user picks a
  // mode, or the form shows "Scheduled Ingestion" for a triggered source (NEX-22548).
  const userPickedModeRef = useRef(false);
  const inboundTriggersKey = getInboundTriggers(source)
    .map((t) => `${t.triggering_event_type}:${getTriggeringResourceId(t)}`)
    .join(',');
  useEffect(() => {
    if (userPickedModeRef.current) return;
    setSelectType(getModeFromSource(source, field?.name));
    setSelectedFlowTriggers(extractFlowTriggerFromSource(source));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboundTriggersKey, source?.['adaptive_flow'], field?.name]);

  const description =
    selectedType === field?.name
      ? showFlowTriggers
        ? descriptionCron
        : 'Set a schedule for writing files to the destination'
      : getDescriptionTrigger(selectedType);

  const handleChange = ({ value }: { value?: string }, isUserAction = true) => {
    const data = {
      ...config,
      [field?.name]: value,
    };

    if (showFlowTriggers) {
      // Explicit [] (not a missing key) tells the save to remove the source's existing
      // triggers — the config may never have carried them (NEX-22548).
      const hasSavedTriggers = source?.flow_triggers?.some((t) => isInboundTrigger(t, source));
      if (data.flow_triggers?.length > 0 || hasSavedTriggers) {
        data.flow_triggers = [];
      } else {
        delete data.flow_triggers;
      }
    }

    if ('adaptive_flow' in data) {
      data['adaptive_flow'] = false;
      onConfigChanged(data, 'adaptive_flow', false);
    }

    onConfigChanged(data, field?.name, value);
    if (isUserAction) {
      userPickedModeRef.current = true;
      onUserModified?.();
    }
    previousCronRef.current = value;
  };

  const onChooseTriggers = (selected: Resources, resourceType: string = RESOURCE_TYPE_SINK) => {
    const writeTrigger = createWriteTrigger(resourceType);
    const flow_triggers = selected.map(({ id }) => writeTrigger(id));
    const data = { ...config, flow_triggers };

    userPickedModeRef.current = true;
    setSelectedFlowTriggers(flow_triggers);
    onConfigChanged(data, 'flow_triggers', flow_triggers);
    onUserModified?.();
  };

  const containerClass = classnames(styles.container, monitorIntervalContainerClassname);

  const onChangeMode = (value: string) => {
    userPickedModeRef.current = true;
    setSelectType(value);
    if (value === FlowTrigger.EXTERNAL) {
      const defaultOnceCron = getDefaultOnceCron();
      const data = {
        ...config,
        [field?.name]: defaultOnceCron,
        ['adaptive_flow']: true,
      };

      // clear the selected flow triggers when switching to external trigger
      setSelectedFlowTriggers([]);
      onConfigChanged(data, 'adaptive_flow', true);
      onConfigChanged(data, field?.name, defaultOnceCron);
    } else if (value === FlowTrigger.SOURCE || value === FlowTrigger.SINK) {
      const defaultOnceCron = getDefaultOnceCron();
      const data = {
        ...config,
        [field?.name]: defaultOnceCron,
        flow_triggers: selectedFlowTriggers,
      };

      if ('adaptive_flow' in data) {
        data['adaptive_flow'] = false;
        onConfigChanged(data, 'adaptive_flow', false);
      }

      onConfigChanged(data, field?.name, defaultOnceCron);
    } else {
      handleChange({ value: previousCronRef.current }, false);
    }
  };

  const defaultSelected = useMemo(() => {
    const listOfTriggers = selectedFlowTriggers?.length
      ? selectedFlowTriggers
      : getInboundTriggers(source);

    return listOfTriggers?.length ? getTriggers(listOfTriggers, sources, sinks) : [];
  }, [source?.flow_triggers, sources, sinks, loading]);

  // Guard AFTER every hook above so the hook count/order stays stable across
  // renders — `field` can be undefined before the form hydrates (e.g. BYO source
  // edit opens the Configure step immediately). Returning early above any hook
  // triggers "Rendered more hooks than during the previous render".
  if (!field || field.name === undefined) return null;

  const isItemDisabled = (item: any) => {
    if (isRagNode(item?.node_type)) {
      return true;
    }

    return (
      source?.origin_node_id === item?.origin_node_id ||
      /**
       * If the source serves as a trigger for a particular resource,
       * that source cannot select this particular resource as a trigger.
       */
      source?.flow_triggers?.some((trigger) => trigger.triggered_resource_id === item.id)
    );
  };

  const filter = (i: any) => (selectedType.includes('source') ? i.isSource : i.isSink);
  const selectionFiltered = defaultSelected.filter(filter);

  const renderTriggerTypes = () => {
    switch (selectedType) {
      case field.name: {
        return (
          <FrequencyFieldRedesign
            name={field.name}
            label={field.label || field?.display_name || 'Schedule Ingestion'}
            value={config?.[field.name]}
            onChange={handleChange}
          />
        );
      }

      case FlowTrigger.SOURCE: {
        return (
          <ResourceSelectionButton
            key={selectedType}
            isItemDisabled={isItemDisabled}
            onChoose={onChooseTriggers}
            defaultSelected={selectionFiltered}
            resourceType={RESOURCE_TYPE_SOURCE}
          />
        );
      }

      case FlowTrigger.SINK: {
        return (
          <ResourceSelectionButton
            key={selectedType}
            isItemDisabled={isItemDisabled}
            onChoose={onChooseTriggers}
            defaultSelected={selectionFiltered}
            resourceType={RESOURCE_TYPE_SINK}
          />
        );
      }

      case FlowTrigger.EXTERNAL: {
        return <ExternalTriggerConfig data={source} />;
      }
    }
  };

  return (
    <div className={containerClass}>
      {showFlowTriggers && (
        <PillTabs
          tabs={tabs(field.name, showExternalTrigger)}
          onSelectionChange={onChangeMode}
          selected={selectedType}
        />
      )}

      <Typography variant="body2" component="p" className={styles.typographySubtitleDescription}>
        {description}
      </Typography>

      {renderTriggerTypes()}
    </div>
  );
};

export default MonitoringInterval;
